import {
  useCallback,
  useEffect,
  useState,
  useRef,
} from "react";

import {
  fetchPortfolioFinancing,
  fetchFinancingStrategies,
} from "../../services/riskFinancingApi";

const DEFAULT_STRATEGY = {
  premium: 0,
  deductible: 0,
  policy_limit: 0,
  coinsurance: 1.0,
};

export default function useRiskFinancingData({
  settingsLoaded,
  simulations,
  strategyAnalysisEnabled = true,
}) {

  const instanceId = useRef(
    Math.random().toString(36).slice(2, 8)
  );

  console.log(
    "### RF DATA INSTANCE ###",
    instanceId.current
  );

  const [strategy, setStrategy] =
    useState(DEFAULT_STRATEGY);

  const [portfolioData, setPortfolioData] =
    useState(null);

  const [decisionData, setDecisionData] =
    useState(null);

  const [initialLoading, setInitialLoading] =
    useState(false);

  const [portfolioLoading, setPortfolioLoading] =
    useState(false);

  const [decisionLoading, setDecisionLoading] =
    useState(false);

  const [portfolioError, setPortfolioError] =
    useState(null);

  const [decisionError, setDecisionError] =
    useState(null);

  const loadPortfolioFinancing = useCallback(
    async (currentStrategy) => {
      const strategyToEvaluate =
        currentStrategy ?? strategy;

      try {
        setPortfolioLoading(true);
        setPortfolioError(null);

        const data = await fetchPortfolioFinancing(
          strategyToEvaluate,
          simulations
        );

        setPortfolioData(data);

        return data;
      } catch (error) {
        console.error(
          "Risk Financing portfolio error:",
          error
        );

        setPortfolioError(error);
        return null;
      } finally {
        setPortfolioLoading(false);
      }
    },
    [strategy, simulations]
  );

  const loadDecisionAnalysis =
    useCallback(async () => {
      try {
        setDecisionLoading(true);
        setDecisionError(null);

        const data =
          await fetchFinancingStrategies(
            simulations
          );

        setDecisionData(data);

        return data;
      } catch (error) {
        console.error(
          "Risk Financing decision analysis error:",
          error
        );

        setDecisionError(error);
        return null;
      } finally {
        setDecisionLoading(false);
      }
    }, [simulations]);

  const loadInitialData = useCallback(async () => {
    try {
      setInitialLoading(true);
      setPortfolioError(null);
      setDecisionError(null);

      const requests = [
        fetchPortfolioFinancing(
          null,
          simulations
        ),
      ];

      if (strategyAnalysisEnabled) {
        requests.push(
          fetchFinancingStrategies(simulations)
        );
      }

      const results = await Promise.allSettled(
        requests
      );

      console.log(
          "### INITIAL RESULTS ###",
          results
      );


      const portfolioResult = results[0];
      const decisionResult = results[1];

      console.log(
          "### PORTFOLIO RESULT ###",
          portfolioResult
      );

      console.log(
        "### PORTFOLIO VALUE ###",
        instanceId.current,
        portfolioResult.value
      );

      if (portfolioResult.status === "fulfilled") {
        const portfolioValue =
          portfolioResult.value;

        setPortfolioData(portfolioValue);

        const returnedPolicy =
          portfolioValue?.policy;

        if (returnedPolicy) {
          setStrategy({
            premium: Number(
              returnedPolicy.premium ?? 0
            ),

            deductible: Number(
              returnedPolicy.deductible ?? 0
            ),

            policy_limit: Number(
              returnedPolicy.policy_limit ?? 0
            ),

            coinsurance: Number(
              returnedPolicy.coinsurance ?? 1
            ),
          });
        }
      }

      if (decisionResult?.status === "fulfilled") {
        setDecisionData(decisionResult.value);
      } else if (decisionResult) {
        console.error(
          "Risk Financing decision initial load error:",
          decisionResult.reason
        );

        setDecisionError(
          decisionResult.reason
        );
      }
    } finally {
      setInitialLoading(false);
    }
  }, [simulations, strategyAnalysisEnabled]);

  useEffect(() => {
    if (!settingsLoaded) return;

    if (
      !Number.isFinite(simulations) ||
      simulations <= 0
    ) {
      return;
    }

    loadInitialData();
  }, [
    settingsLoaded,
    simulations,
    loadInitialData,
  ]);

  const handleStrategyChange = useCallback(
    (field, value) => {
      setStrategy((previousStrategy) => ({
        ...previousStrategy,
        [field]: Number(value),
      }));
    },
    []
  );

  const recalculatePortfolio =
    useCallback(() => {
      return loadPortfolioFinancing(strategy);
    }, [loadPortfolioFinancing, strategy]);

  return {
    strategy,
    portfolioData,
    decisionData,

    initialLoading,
    portfolioLoading,
    decisionLoading,

    portfolioError,
    decisionError,

    handleStrategyChange,
    recalculatePortfolio,
    loadPortfolioFinancing,
    loadDecisionAnalysis,
    refresh: loadInitialData,
  };
}
